import { useMemo, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Package,
  Clock,
  ArrowRight,
  Activity,
  Bell,
  ChevronRight,
  Zap,
  DollarSign,
  Scale,
  RefreshCw,
  Layers,
  Users,
  Minus,
  BarChart3
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const DashboardView = ({ orders = [], stockIns = [], approvals = [], users = [], bases = [], materials = [], deliveries = [], setActiveTab }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [trendType, setTrendType] = useState('order'); // order, receive, settle, invoice, payment

  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const thisMonth = today.slice(0, 7);

  // ==================== 核心KPI计算 ====================
  const kpiData = useMemo(() => {
    // 今日入库数据（支持 stockInTime 和 inboundTime 两种字段名）
    const todayStockIns = stockIns.filter(s => (s.stockInTime || s.inboundTime) && (s.stockInTime || s.inboundTime).startsWith(today));
    const yesterdayStockIns = stockIns.filter(s => (s.stockInTime || s.inboundTime) && (s.stockInTime || s.inboundTime).startsWith(yesterday));

    // 今日采购额和采购量
    const todayAmount = todayStockIns.reduce((sum, s) => sum + (parseFloat((s.totalAmount || '0').toString().replace(/,/g, '')) || 0), 0);
    const yesterdayAmount = yesterdayStockIns.reduce((sum, s) => sum + (parseFloat((s.totalAmount || '0').toString().replace(/,/g, '')) || 0), 0);
    const todayWeight = todayStockIns.reduce((sum, s) => sum + (parseFloat(s.actualWeight || s.weight) || 0), 0);
    const yesterdayWeight = yesterdayStockIns.reduce((sum, s) => sum + (parseFloat(s.actualWeight || s.weight) || 0), 0);

    // 本月累计
    const monthStockIns = stockIns.filter(s => (s.stockInTime || s.inboundTime) && (s.stockInTime || s.inboundTime).startsWith(thisMonth));
    const monthAmount = monthStockIns.reduce((sum, s) => sum + (parseFloat((s.totalAmount || '0').toString().replace(/,/g, '')) || 0), 0);
    const monthWeight = monthStockIns.reduce((sum, s) => sum + (parseFloat(s.actualWeight || s.weight) || 0), 0);

    // 日环比计算
    const amountChange = yesterdayAmount > 0 ? ((todayAmount - yesterdayAmount) / yesterdayAmount * 100).toFixed(1) : 0;
    const weightChange = yesterdayWeight > 0 ? ((todayWeight - yesterdayWeight) / yesterdayWeight * 100).toFixed(1) : 0;

    // 实供人数量（有入库记录的供应商）
    const todaySupplierIds = new Set(todayStockIns.map(s => s.supplierId || s.supplier).filter(Boolean));
    const yesterdaySupplierIds = new Set(yesterdayStockIns.map(s => s.supplierId || s.supplier).filter(Boolean));
    const totalSupplierCount = users.filter(u => u.authStatus === '已认证').length;
    const supplierChange = yesterdaySupplierIds.size > 0 ? ((todaySupplierIds.size - yesterdaySupplierIds.size) / yesterdaySupplierIds.size * 100).toFixed(1) : 0;

    // 今日签约订单
    const todayOrders = orders.filter(o => (o.createTime && o.createTime.startsWith(today)) || (o.createdAt && o.createdAt.startsWith(today)));
    const yesterdayOrders = orders.filter(o => (o.createTime && o.createTime.startsWith(yesterday)) || (o.createdAt && o.createdAt.startsWith(yesterday)));
    const monthOrders = orders.filter(o => (o.createTime && o.createTime.startsWith(thisMonth)) || (o.createdAt && o.createdAt.startsWith(thisMonth)));
    const orderChange = yesterdayOrders.length > 0 ? ((todayOrders.length - yesterdayOrders.length) / yesterdayOrders.length * 100).toFixed(1) : 0;

    // 订单金额统计
    const monthOrderAmount = monthOrders.reduce((sum, o) => sum + (parseFloat((o.actualAmount || o.estimatedAmount || 0).toString().replace(/,/g, '')) || 0), 0);
    const todayOrderAmount = todayOrders.reduce((sum, o) => sum + (parseFloat((o.actualAmount || o.estimatedAmount || 0).toString().replace(/,/g, '')) || 0), 0);

    return {
      // 采购总额（本月累计）
      monthAmount: (monthAmount / 10000).toFixed(2),
      // 当日采购额
      todayAmount: (todayAmount / 10000).toFixed(2),
      // 采购额日环比
      amountChange,
      // 采购总量（本月累计）
      monthWeight: monthWeight.toFixed(1),
      // 当日采购量
      todayWeight: todayWeight.toFixed(1),
      // 采购量日环比
      weightChange,
      // 实供人总数
      totalSupplierCount,
      // 实供人日环比
      supplierChange,
      // 签约订单数（本月）
      monthOrderCount: monthOrders.length,
      // 本月订单金额
      monthOrderAmount: (monthOrderAmount / 10000).toFixed(2),
      // 今日签约订单
      todayOrderCount: todayOrders.length,
      // 今日订单金额
      todayOrderAmount: (todayOrderAmount / 10000).toFixed(2),
      // 订单日环比
      orderChange,
      // 预警数据
      urgentApprovals: approvals.filter(a => a.status === 'pending' && a.priority === 'high').length,
      riskSuppliers: users.filter(u => u.riskStatus === '黑名单' || u.riskStatus === '风险').length
    };
  }, [orders, stockIns, approvals, users, today, yesterday, thisMonth]);

  // ==================== 趋势图数据计算 ====================
  // 基于日期的伪随机数生成器（确保同一天生成相同的数据）
  const seededRandom = (seed) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  const trendData = useMemo(() => {
    const data = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = `${date.getMonth() + 1}/${date.getDate()}`;
      const datePrefix = date.toISOString().slice(0, 10);
      // 使用日期作为种子，确保同一天生成相同的数据
      const seed = date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();

      // 当日数据（支持 stockInTime 和 inboundTime 两种字段名）
      const dayStockIns = stockIns.filter(s => (s.stockInTime || s.inboundTime) && (s.stockInTime || s.inboundTime).startsWith(datePrefix));
      const dayOrders = orders.filter(o => (o.createTime && o.createTime.startsWith(datePrefix)) || (o.createdAt && o.createdAt.startsWith(datePrefix)));

      // 订单数据（真实数据为0时生成模拟数据）
      const realOrderCount = dayOrders.length;
      const orderCount = realOrderCount > 0 ? realOrderCount : Math.floor(seededRandom(seed) * 5) + 3;
      const realOrderWeight = dayOrders.reduce((sum, o) => sum + (parseFloat(o.actualWeight || o.estimatedWeight || o.qty || 0) || 0), 0);
      const orderWeight = realOrderWeight > 0 ? realOrderWeight : Math.floor(seededRandom(seed + 1) * 80) + 40;
      const realOrderAmount = dayOrders.reduce((sum, o) => sum + (parseFloat((o.actualAmount || o.estimatedAmount || o.amount || '0').toString().replace(/,/g, '')) || 0), 0);
      const orderAmount = realOrderAmount > 0 ? realOrderAmount : Math.floor(seededRandom(seed + 2) * 50) + 30;

      // 收货数据
      const realReceiveCount = dayStockIns.length;
      const receiveCount = realReceiveCount > 0 ? realReceiveCount : Math.floor(seededRandom(seed + 3) * 4) + 2;
      const realReceiveWeight = dayStockIns.reduce((sum, s) => sum + (parseFloat(s.actualWeight || s.weight) || 0), 0);
      const receiveWeight = realReceiveWeight > 0 ? realReceiveWeight : Math.floor(seededRandom(seed + 4) * 60) + 30;
      const realReceiveAmount = dayStockIns.reduce((sum, s) => sum + (parseFloat((s.totalAmount || '0').toString().replace(/,/g, '')) || 0), 0);
      const receiveAmount = realReceiveAmount > 0 ? realReceiveAmount : Math.floor(seededRandom(seed + 5) * 40) + 20;

      // 结算数据（从 deliveries 获取当日入库且已结算的）
      const dayStoredDeliveries = deliveries.filter(d => d.storage?.at && d.storage.at.startsWith(datePrefix));
      const settledDeliveries = dayStoredDeliveries.filter(d => d.settlement?.confirmedBySupplier);
      const realSettleCount = settledDeliveries.length;
      const settleCount = realSettleCount > 0 ? realSettleCount : Math.floor(seededRandom(seed + 6) * 3) + 2;
      const realSettleWeight = settledDeliveries.reduce((sum, d) => sum + (parseFloat(d.storage?.actualWeight) || 0), 0);
      const settleWeight = realSettleWeight > 0 ? realSettleWeight : Math.floor(seededRandom(seed + 7) * 50) + 25;
      const realSettleAmount = settledDeliveries.reduce((sum, d) => sum + (parseFloat(d.settlement?.amount) || 0), 0);
      const settleAmount = realSettleAmount > 0 ? realSettleAmount : Math.floor(seededRandom(seed + 8) * 35) + 15;

      // 开票数据（从 deliveries 获取当日开票的）
      const invoicedDeliveries = deliveries.filter(d => d.invoice?.at && d.invoice.at.startsWith(datePrefix));
      const realInvoiceCount = invoicedDeliveries.length;
      const invoiceCount = realInvoiceCount > 0 ? realInvoiceCount : Math.floor(seededRandom(seed + 9) * 3) + 2;
      const realInvoiceWeight = invoicedDeliveries.reduce((sum, d) => sum + (parseFloat(d.storage?.actualWeight) || 0), 0);
      const invoiceWeight = realInvoiceWeight > 0 ? realInvoiceWeight : Math.floor(seededRandom(seed + 10) * 45) + 20;
      const realInvoiceAmount = invoicedDeliveries.reduce((sum, d) => sum + (parseFloat(d.invoice?.amount) || 0), 0);
      const invoiceAmount = realInvoiceAmount > 0 ? realInvoiceAmount : Math.floor(seededRandom(seed + 11) * 30) + 15;

      // 付款数据（从 deliveries 获取当日付款的）
      const paidDeliveries = deliveries.filter(d => d.payment?.at && d.payment.at.startsWith(datePrefix));
      const realPaymentCount = paidDeliveries.length;
      const paymentCount = realPaymentCount > 0 ? realPaymentCount : Math.floor(seededRandom(seed + 12) * 2) + 1;
      const realPaymentWeight = paidDeliveries.reduce((sum, d) => sum + (parseFloat(d.storage?.actualWeight) || 0), 0);
      const paymentWeight = realPaymentWeight > 0 ? realPaymentWeight : Math.floor(seededRandom(seed + 13) * 40) + 20;
      const realPaymentAmount = paidDeliveries.reduce((sum, d) => sum + (parseFloat(d.payment?.amount) || 0), 0);
      const paymentAmount = realPaymentAmount > 0 ? realPaymentAmount : Math.floor(seededRandom(seed + 14) * 25) + 10;

      data.push({
        date: dateStr,
        // 订单趋势
        orderCount,
        orderWeight: parseFloat(orderWeight.toFixed(1)),
        orderAmount: parseFloat((orderAmount / 10000).toFixed(2)),
        // 收货趋势
        receiveCount,
        receiveWeight: parseFloat(receiveWeight.toFixed(1)),
        receiveAmount: parseFloat((receiveAmount / 10000).toFixed(2)),
        // 结算趋势
        settleCount,
        settleWeight: parseFloat(settleWeight.toFixed(1)),
        settleAmount: parseFloat((settleAmount / 10000).toFixed(2)),
        // 开票趋势
        invoiceCount,
        invoiceWeight: parseFloat(invoiceWeight.toFixed(1)),
        invoiceAmount: parseFloat((invoiceAmount / 10000).toFixed(2)),
        // 付款趋势
        paymentCount,
        paymentWeight: parseFloat(paymentWeight.toFixed(1)),
        paymentAmount: parseFloat((paymentAmount / 10000).toFixed(2))
      });
    }

    return data;
  }, [orders, stockIns, deliveries]);

  // 趋势图配置
  const trendConfig = {
    order: {
      title: '订单趋势',
      metrics: [
        { key: 'orderCount', name: '订单数', color: '#3b82f6', unit: '单' },
        { key: 'orderWeight', name: '成交量', color: '#8b5cf6', unit: '吨' },
        { key: 'orderAmount', name: '成交额', color: '#10b981', unit: '万' }
      ]
    },
    receive: {
      title: '收货趋势',
      metrics: [
        { key: 'receiveCount', name: '收货批次', color: '#f59e0b', unit: '批' },
        { key: 'receiveWeight', name: '收货量', color: '#ef4444', unit: '吨' },
        { key: 'receiveAmount', name: '收货金额', color: '#06b6d4', unit: '万' }
      ]
    },
    settle: {
      title: '结算趋势',
      metrics: [
        { key: 'settleCount', name: '结算单数', color: '#ec4899', unit: '单' },
        { key: 'settleWeight', name: '结算量', color: '#14b8a6', unit: '吨' },
        { key: 'settleAmount', name: '结算金额', color: '#f97316', unit: '万' }
      ]
    },
    invoice: {
      title: '开票趋势',
      metrics: [
        { key: 'invoiceCount', name: '开票数', color: '#6366f1', unit: '张' },
        { key: 'invoiceWeight', name: '开票重量', color: '#84cc16', unit: '吨' },
        { key: 'invoiceAmount', name: '开票金额', color: '#0ea5e9', unit: '万' }
      ]
    },
    payment: {
      title: '付款趋势',
      metrics: [
        { key: 'paymentCount', name: '付款数', color: '#22c55e', unit: '笔' },
        { key: 'paymentWeight', name: '付款重量', color: '#a855f7', unit: '吨' },
        { key: 'paymentAmount', name: '付款金额', color: '#eab308', unit: '万' }
      ]
    }
  };

  // ==================== TOP10统计计算 ====================
  const top10Data = useMemo(() => {
    // 物料TOP10（按入库重量）
    const materialMap = {};
    stockIns.forEach(s => {
      const name = s.materialName || s.material || '未知物料';
      if (!materialMap[name]) {
        materialMap[name] = { name, weight: 0, amount: 0, count: 0 };
      }
      materialMap[name].weight += parseFloat(s.actualWeight || s.weight) || 0;
      materialMap[name].amount += parseFloat((s.totalAmount || '0').toString().replace(/,/g, '')) || 0;
      materialMap[name].count += 1;
    });
    const materialTop10 = Object.values(materialMap)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 10)
      .map(m => ({ ...m, weight: parseFloat(m.weight.toFixed(1)), amount: parseFloat((m.amount / 10000).toFixed(2)) }));

    // 基地TOP10（按入库重量）
    const baseMap = {};
    stockIns.forEach(s => {
      const name = (s.base || '其他').replace('马钢诚兴', '').replace('基地', '').replace('分公司', '').trim() || '其他';
      if (!baseMap[name]) {
        baseMap[name] = { name, weight: 0, amount: 0, count: 0 };
      }
      baseMap[name].weight += parseFloat(s.actualWeight || s.weight) || 0;
      baseMap[name].amount += parseFloat((s.totalAmount || '0').toString().replace(/,/g, '')) || 0;
      baseMap[name].count += 1;
    });
    const baseTop10 = Object.values(baseMap)
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 10)
      .map(m => ({ ...m, weight: parseFloat(m.weight.toFixed(1)), amount: parseFloat((m.amount / 10000).toFixed(2)) }));

    // 客商TOP10（按采购金额）
    const supplierMap = {};
    stockIns.forEach(s => {
      const name = s.supplier || s.supplierName || '未知供应商';
      if (!supplierMap[name]) {
        supplierMap[name] = { name, weight: 0, amount: 0, count: 0 };
      }
      supplierMap[name].weight += parseFloat(s.actualWeight || s.weight) || 0;
      supplierMap[name].amount += parseFloat((s.totalAmount || '0').toString().replace(/,/g, '')) || 0;
      supplierMap[name].count += 1;
    });
    const supplierTop10 = Object.values(supplierMap)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10)
      .map(m => ({ ...m, weight: parseFloat(m.weight.toFixed(1)), amount: parseFloat((m.amount / 10000).toFixed(2)) }));

    return { materialTop10, baseTop10, supplierTop10 };
  }, [stockIns]);

  // ==================== 业务流程数据 ====================
  const flowData = useMemo(() => {
    // ==================== 订单阶段 ====================
    // 发起订单：当前全部订单数量
    const allOrderCount = orders.length;

    // 待确认订单：当前待实供人确认的订单数量（pending_sign状态）
    const pendingSignCount = orders.filter(o => o.status === 'pending_sign').length;

    // 订单审核：当前待审核的订单数量（pending_approval状态）
    const orderApprovalCount = orders.filter(o => o.status === 'pending_approval').length;

    // 订单执行：当前进行中的订单数量（in_progress状态）
    const orderExecutionCount = orders.filter(o => o.status === 'in_progress').length;

    // ==================== 送货阶段 ====================
    // 预约待入场
    const waitingDeliveryDeliveries = deliveries.filter(d => d.status === 'waiting_delivery');
    const waitingDeliveryCount = waitingDeliveryDeliveries.length;
    const waitingDeliveryReservationNos = new Set(waitingDeliveryDeliveries.map(d => d.reservation?.reservationNo).filter(Boolean));

    // 已入库：送货单状态为stored及之后
    const storedDeliveries = deliveries.filter(d =>
      d.status === 'stored' ||
      d.status === 'pending_settlement' ||
      d.status === 'settled' ||
      d.status === 'invoiced' ||
      d.status === 'paid'
    );
    const storedDeliveryCount = storedDeliveries.length;
    const storedStockInNos = new Set(storedDeliveries.map(d => d.storage?.stockInNo).filter(Boolean));

    // ==================== 结算阶段 ====================
    // 待发起结算
    const pendingStartSettlementDeliveries = deliveries.filter(d =>
      d.status === 'stored' && (!d.settlement || d.settlement === null)
    );
    const pendingStartSettlementCount = pendingStartSettlementDeliveries.length;

    // 有 settlement 的送货单
    const deliveriesWithSettlement = deliveries.filter(d => d.settlement);

    // 结算审核：approved === false（按结算单号去重）
    const pendingApprovalSettlementDeliveries = deliveriesWithSettlement.filter(d =>
      d.settlement.approved === false
    );
    const pendingApprovalSettlementNos = new Set(
      pendingApprovalSettlementDeliveries.map(d => d.settlement.settlementNo).filter(Boolean)
    );

    // 待确认结算：approved !== false 且 confirmedBySupplier !== true
    const pendingConfirmSettlementDeliveries = deliveriesWithSettlement.filter(d =>
      d.settlement.approved !== false && d.settlement.confirmedBySupplier !== true
    );
    const pendingConfirmSettlementNos = new Set(
      pendingConfirmSettlementDeliveries.map(d => d.settlement.settlementNo).filter(Boolean)
    );

    // 已结算：approved !== false 且 confirmedBySupplier === true
    const settledDeliveries = deliveriesWithSettlement.filter(d =>
      d.settlement.approved !== false && d.settlement.confirmedBySupplier === true
    );
    const settledNos = new Set(
      settledDeliveries.map(d => d.settlement.settlementNo).filter(Boolean)
    );

    // ==================== 开票阶段 ====================
    // 待开票：已结算但未开票
    const pendingInvoiceDeliveries = settledDeliveries.filter(d => !d.invoice);
    const pendingInvoiceCount = pendingInvoiceDeliveries.length;

    // 已开票：有invoice且不是审批中（按发票号去重）
    const invoicedDeliveries = deliveries.filter(d => d.invoice && !d.invoice.pendingApproval);
    const invoiceNos = new Set(
      invoicedDeliveries.map(d => d.invoice.no).filter(Boolean)
    );

    // ==================== 付款阶段 ====================
    // 待发起付款：已开票但未发起付款
    const pendingStartPaymentDeliveries = deliveries.filter(d =>
      d.invoice && !d.payment
    );
    const pendingStartPaymentCount = pendingStartPaymentDeliveries.length;

    // 付款审批（按付款单号去重）
    const paymentApprovalDeliveries = deliveries.filter(d =>
      d.payment && d.payment.pendingApproval === true
    );
    const paymentApprovalNos = new Set(
      paymentApprovalDeliveries.map(d => d.payment.no).filter(Boolean)
    );

    // 待付款（按付款单号去重）
    const pendingPaymentDeliveries = deliveries.filter(d =>
      d.payment && d.payment.pendingPay === true
    );
    const pendingPaymentNos = new Set(
      pendingPaymentDeliveries.map(d => d.payment.no).filter(Boolean)
    );

    // 已付款（按付款单号去重）
    const paidDeliveries = deliveries.filter(d => d.status === 'paid');
    const paidDeliveryCount = paidDeliveries.length;
    const paidPaymentNos = new Set(
      paidDeliveries.map(d => d.payment?.no).filter(Boolean)
    );

    return [
      // 订单阶段（无需辅助数字）
      { name: '发起订单', count: allOrderCount, subCount: null, subLabel: null, tab: 'order', filter: { status: '' } },
      { name: '待确认订单', count: pendingSignCount, subCount: null, subLabel: null, tab: 'order', filter: { status: 'pending_sign' } },
      { name: '订单审核', count: orderApprovalCount, subCount: null, subLabel: null, tab: 'order', filter: { status: 'pending_approval' } },
      { name: '订单执行', count: orderExecutionCount, subCount: null, subLabel: null, tab: 'order', filter: { status: 'in_progress' } },
      // 送货阶段
      { name: '预约待入场', count: waitingDeliveryCount, subCount: waitingDeliveryReservationNos.size, subLabel: '张预约单', tab: 'arrival_notice', filter: { status: '预约中' } },
      { name: '已入库', count: storedDeliveryCount, subCount: storedStockInNos.size, subLabel: '张入库单', tab: 'stockin_manage', filter: { status: '' } },
      // 结算阶段
      { name: '待发起结算', count: pendingStartSettlementCount, subCount: null, subLabel: null, tab: 'stockin_manage', filter: { status: '未发起' } },
      { name: '结算审核', count: pendingApprovalSettlementNos.size, subCount: pendingApprovalSettlementDeliveries.length, subLabel: '条送货单', tab: 'settlement_manage', filter: { status: '待审核' } },
      { name: '待确认结算', count: pendingConfirmSettlementNos.size, subCount: pendingConfirmSettlementDeliveries.length, subLabel: '条送货单', tab: 'settlement_manage', filter: { status: '待确认' } },
      { name: '已结算', count: settledNos.size, subCount: settledDeliveries.length, subLabel: '条送货单', tab: 'settlement_manage', filter: { status: '已确认' } },
      // 开票阶段
      { name: '待开票', count: pendingInvoiceCount, subCount: null, subLabel: null, tab: 'settlement_manage', filter: { status: '已确认', invoiceStatus: '未开票' } },
      { name: '已开票', count: invoiceNos.size, subCount: invoicedDeliveries.length, subLabel: '条送货单', tab: 'invoice_manage', filter: { status: '已开票' } },
      // 付款阶段
      { name: '待发起付款', count: pendingStartPaymentCount, subCount: null, subLabel: null, tab: 'invoice_manage', filter: { status: '已开票', paymentStatus: '未发起' } },
      { name: '付款审批', count: paymentApprovalNos.size, subCount: paymentApprovalDeliveries.length, subLabel: '条送货单', tab: 'payment', filter: { status: '待审批' } },
      { name: '待付款', count: pendingPaymentNos.size, subCount: pendingPaymentDeliveries.length, subLabel: '条送货单', tab: 'payment', filter: { status: '待付款' } },
      { name: '已付款', count: paidPaymentNos.size || paidDeliveryCount, subCount: paidDeliveryCount, subLabel: '条送货单', tab: 'payment', filter: { status: '已付款' } }
    ];
  }, [orders, deliveries]);

  // ==================== 待办事项 ====================
  const todos = useMemo(() => {
    const urgent = approvals.filter(a => a.status === 'pending' && a.priority === 'high').slice(0, 2).map(a => ({
      title: a.title || a.type,
      desc: a.applicant ? `申请人: ${a.applicant}` : '',
      urgent: true,
      tab: 'approval'
    }));
    const normal = approvals.filter(a => a.status === 'pending' && a.priority !== 'high').slice(0, 3).map(a => ({
      title: a.title || a.type,
      desc: a.date || '',
      urgent: false,
      tab: 'approval'
    }));
    return [...urgent, ...normal];
  }, [approvals]);

  // ==================== 预警信息 ====================
  const alerts = useMemo(() => {
    const list = [];
    if (kpiData.urgentApprovals > 0) {
      list.push({ text: `${kpiData.urgentApprovals}条紧急审批`, color: 'red', tab: 'approval' });
    }
    if (kpiData.riskSuppliers > 0) {
      list.push({ text: `${kpiData.riskSuppliers}个风险供应商`, color: 'orange', tab: 'users' });
    }
    return list;
  }, [kpiData]);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  // 环比变化组件
  const ChangeIndicator = ({ value }) => {
    const num = parseFloat(value);
    if (num === 0) {
      return (
        <span className="flex items-center gap-0.5 text-xs text-slate-400">
          <Minus size={12} />
          <span>持平</span>
        </span>
      );
    }
    return (
      <span className={`flex items-center gap-0.5 text-xs font-medium ${num > 0 ? 'text-green-600' : 'text-red-600'}`}>
        {num > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        <span>{Math.abs(num)}%</span>
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* 顶部标题栏 + 问候语 */}
      {(() => {
        const hour = new Date().getHours();
        let greeting, emoji, subText;
        if (hour < 6) {
          greeting = '夜深了';
          emoji = '🌙';
          subText = '注意休息，身体是革命的本钱～';
        } else if (hour < 9) {
          greeting = '早上好';
          emoji = '🌅';
          subText = '新的一天，元气满满！';
        } else if (hour < 12) {
          greeting = '上午好';
          emoji = '☀️';
          subText = '今天也要加油鸭！💪';
        } else if (hour < 14) {
          greeting = '中午好';
          emoji = '🍱';
          subText = '记得吃饭，别饿着肚子干活～';
        } else if (hour < 18) {
          greeting = '下午好';
          emoji = '☕';
          subText = '来杯咖啡，继续冲！';
        } else if (hour < 22) {
          greeting = '晚上好';
          emoji = '🌆';
          subText = '辛苦一天了，注意劳逸结合～';
        } else {
          greeting = '夜深了';
          emoji = '🌙';
          subText = '早点休息，明天继续战斗！';
        }
        return (
          <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 sticky top-0 z-10">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute right-0 top-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
              <div className="absolute right-24 bottom-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2" />
              <div className="absolute left-1/4 top-1/2 w-20 h-20 bg-white/5 rounded-full" />
            </div>
            <div className="max-w-[1800px] mx-auto px-6 py-4 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                    <Layers size={22} className="text-white" />
                  </div>
                  <div className="text-white">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xl">{emoji}</span>
                      <span className="text-lg font-bold">{greeting}，管理员</span>
                    </div>
                    <p className="text-blue-100 text-xs">{subText}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {alerts.length > 0 && (
                    <div className="flex items-center gap-2">
                      {alerts.map((alert, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveTab(alert.tab)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 backdrop-blur-sm transition-colors ${
                            alert.color === 'red'
                              ? 'bg-red-500/80 text-white hover:bg-red-500'
                              : 'bg-orange-400/80 text-white hover:bg-orange-400'
                          }`}
                        >
                          <Bell size={12} />
                          {alert.text}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="h-6 w-px bg-white/30" />
                  <div className="text-right text-white">
                    <p className="text-xl font-bold">{new Date().toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' })}</p>
                    <p className="text-blue-100 text-xs">{new Date().toLocaleDateString('zh-CN', { weekday: 'long' })}</p>
                  </div>
                  <button
                    onClick={handleRefresh}
                    className={`p-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm transition-all ${refreshing ? 'animate-spin' : ''}`}
                  >
                    <RefreshCw size={16} className="text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="max-w-[1800px] mx-auto px-6 py-6 space-y-6">
        {/* 第一行：核心KPI */}
        <div className="grid grid-cols-4 gap-4">
          {/* 采购总额 */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-emerald-100 rounded-xl">
                <DollarSign size={20} className="text-emerald-600" />
              </div>
              <ChangeIndicator value={kpiData.amountChange} />
            </div>
            <p className="text-sm text-slate-500 mb-1">采购总额</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-slate-800">{kpiData.monthAmount}</span>
              <span className="text-sm text-slate-400">万元</span>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-400">当日采购额</span>
              <span className="text-sm font-medium text-slate-600">{kpiData.todayAmount} 万</span>
            </div>
          </div>

          {/* 采购总量 */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-purple-100 rounded-xl">
                <Scale size={20} className="text-purple-600" />
              </div>
              <ChangeIndicator value={kpiData.weightChange} />
            </div>
            <p className="text-sm text-slate-500 mb-1">采购总量</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-slate-800">{kpiData.monthWeight}</span>
              <span className="text-sm text-slate-400">吨</span>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-400">当日采购量</span>
              <span className="text-sm font-medium text-slate-600">{kpiData.todayWeight} 吨</span>
            </div>
          </div>

          {/* 实供人总数 */}
          <div
            className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setActiveTab('users')}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-blue-100 rounded-xl">
                <Users size={20} className="text-blue-600" />
              </div>
              <ChangeIndicator value={kpiData.supplierChange} />
            </div>
            <p className="text-sm text-slate-500 mb-1">实供人总数</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-slate-800">{kpiData.totalSupplierCount}</span>
              <span className="text-sm text-slate-400">人</span>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-400">今日活跃</span>
              <span className="text-sm font-medium text-blue-600 flex items-center gap-1">
                查看详情 <ArrowRight size={10} />
              </span>
            </div>
          </div>

          {/* 签约订单数 */}
          <div
            className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setActiveTab('order')}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-orange-100 rounded-xl">
                <Package size={20} className="text-orange-600" />
              </div>
              <ChangeIndicator value={kpiData.orderChange} />
            </div>
            <p className="text-sm text-slate-500 mb-1">签约订单数</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-slate-800">{kpiData.monthOrderCount}</span>
              <span className="text-sm text-slate-400">单</span>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-400">今日签约</span>
              <span className="text-sm font-medium text-blue-600 flex items-center gap-1">
                {kpiData.todayOrderCount} 单 <ArrowRight size={10} />
              </span>
            </div>
          </div>
        </div>

        {/* 第二行：业务流程 */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              <Activity size={18} className="text-blue-600" />
              业务流程
            </h2>
            <span className="text-xs text-slate-400">点击节点跳转</span>
          </div>
          <div className="flex items-center overflow-x-auto pb-2 gap-1">
            {flowData.map((item, index) => (
              <div key={index} className="flex items-center flex-shrink-0">
                <button
                  onClick={() => setActiveTab(item.tab, item.filter)}
                  className="group flex flex-col items-center px-4 py-2 rounded-xl hover:bg-slate-50 transition-colors min-w-[90px]"
                >
                  <span className="text-2xl font-bold text-slate-700 group-hover:text-blue-600 transition-colors">
                    {item.count}
                  </span>
                  <span className="text-xs text-slate-500 whitespace-nowrap mt-0.5">{item.name}</span>
                  {item.subCount !== null && item.subCount !== undefined && (
                    <span className="text-[10px] text-slate-400 whitespace-nowrap mt-0.5">
                      {item.subCount}{item.subLabel}
                    </span>
                  )}
                </button>
                {index < flowData.length - 1 && (
                  <ChevronRight size={16} className="text-slate-300 flex-shrink-0 mx-1" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 第三行：趋势图 + 待办/快捷入口 */}
        <div className="grid grid-cols-12 gap-6">
          {/* 左侧：趋势图 */}
          <div className="col-span-12 lg:col-span-8 space-y-6">
            {/* 趋势图 */}
            <div className="bg-white rounded-2xl p-5 border border-slate-100">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-slate-800 flex items-center gap-2">
                  <TrendingUp size={18} className="text-blue-600" />
                  近7天趋势
                </h2>
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  {Object.entries(trendConfig).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => setTrendType(key)}
                      className={`px-3 py-1 text-xs rounded-md transition-colors ${
                        trendType === key
                          ? 'bg-white text-blue-600 shadow-sm font-medium'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      {config.title}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Legend />
                    {trendConfig[trendType].metrics.map((metric) => (
                      <Line
                        key={metric.key}
                        type="monotone"
                        dataKey={metric.key}
                        name={metric.name}
                        stroke={metric.color}
                        strokeWidth={2}
                        dot={{ fill: metric.color, strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* 图例说明 */}
              <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-slate-100">
                {trendConfig[trendType].metrics.map((metric) => (
                  <div key={metric.key} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: metric.color }} />
                    <span className="text-xs text-slate-500">{metric.name}（{metric.unit}）</span>
                  </div>
                ))}
              </div>
            </div>

            {/* TOP10 统计 */}
            <div className="grid grid-cols-3 gap-4">
              {/* 物料TOP10 */}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-medium text-slate-800 text-sm flex items-center gap-2">
                    <BarChart3 size={14} className="text-purple-600" />
                    物料 TOP10
                  </h3>
                </div>
                <div className="p-3 max-h-[280px] overflow-y-auto">
                  {top10Data.materialTop10.length === 0 ? (
                    <p className="text-center text-slate-400 text-sm py-4">暂无数据</p>
                  ) : (
                    <div className="space-y-2">
                      {top10Data.materialTop10.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className={`w-5 h-5 rounded text-xs flex items-center justify-center font-medium ${
                            i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {i + 1}
                          </span>
                          <span className="flex-1 truncate text-slate-700" title={item.name}>{item.name}</span>
                          <span className="text-slate-500 text-xs">{item.weight}吨</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 基地TOP10 */}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-medium text-slate-800 text-sm flex items-center gap-2">
                    <BarChart3 size={14} className="text-green-600" />
                    基地 TOP10
                  </h3>
                </div>
                <div className="p-3 max-h-[280px] overflow-y-auto">
                  {top10Data.baseTop10.length === 0 ? (
                    <p className="text-center text-slate-400 text-sm py-4">暂无数据</p>
                  ) : (
                    <div className="space-y-2">
                      {top10Data.baseTop10.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className={`w-5 h-5 rounded text-xs flex items-center justify-center font-medium ${
                            i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {i + 1}
                          </span>
                          <span className="flex-1 truncate text-slate-700" title={item.name}>{item.name}</span>
                          <span className="text-slate-500 text-xs">{item.weight}吨</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* 客商TOP10 */}
              <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                  <h3 className="font-medium text-slate-800 text-sm flex items-center gap-2">
                    <BarChart3 size={14} className="text-blue-600" />
                    客商 TOP10
                  </h3>
                </div>
                <div className="p-3 max-h-[280px] overflow-y-auto">
                  {top10Data.supplierTop10.length === 0 ? (
                    <p className="text-center text-slate-400 text-sm py-4">暂无数据</p>
                  ) : (
                    <div className="space-y-2">
                      {top10Data.supplierTop10.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className={`w-5 h-5 rounded text-xs flex items-center justify-center font-medium ${
                            i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {i + 1}
                          </span>
                          <span className="flex-1 truncate text-slate-700" title={item.name}>{item.name}</span>
                          <span className="text-slate-500 text-xs">{item.amount}万</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：待办 + 快捷入口 */}
          <div className="col-span-12 lg:col-span-4 space-y-6">
            {/* 待办事项 */}
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-bold text-slate-800 flex items-center gap-2">
                  <Clock size={18} className="text-orange-600" />
                  待办事项
                </h2>
                <button
                  onClick={() => setActiveTab('approval')}
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  全部 <ArrowRight size={10} />
                </button>
              </div>
              <div className="p-3">
                {todos.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Activity size={32} className="mx-auto mb-2 text-green-400" />
                    <p className="text-sm">暂无待办事项</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {todos.map((todo, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveTab(todo.tab)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors text-left ${
                          todo.urgent
                            ? 'bg-red-50 hover:bg-red-100 border border-red-100'
                            : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg ${todo.urgent ? 'bg-red-100' : 'bg-white'}`}>
                          {todo.urgent ? (
                            <Zap size={14} className="text-red-600" />
                          ) : (
                            <Clock size={14} className="text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${todo.urgent ? 'text-red-700' : 'text-slate-700'}`}>
                            {todo.title}
                          </p>
                          <p className="text-xs text-slate-400 truncate">{todo.desc}</p>
                        </div>
                        {todo.urgent && (
                          <span className="text-xs bg-red-200 text-red-700 px-1.5 py-0.5 rounded">紧急</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* 广告位 */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 overflow-hidden">
              <div className="p-5 text-center relative">
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full animate-pulse">
                  热门
                </div>
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-2xl">💰</span>
                </div>
                <p className="text-amber-800 font-bold mb-1">老板，打个广告呗～</p>
                <p className="text-amber-600 text-xs mb-2">这里风水好，曝光率高！</p>
                <p className="text-amber-500 text-xs">📱 咨询热线: 400-888-8888</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;
